import React from 'react';
import {
	GatsbyImage as GI,
	getImage,
	withArtDirection
} from 'gatsby-plugin-image';

/**
 *  *********  Query for images sourced from WP (ACF Image clone): *********
 *
 *  NOTE: for art directed images, GatsbyImage ignores aspect ratio and only uses
 *  the first image aspect ratio. We'll keep using our aspect-ratio-media mixin for these.
 *  in combination with setting gatsbyImageData(layout: FULL_WIDTH)
 *
 *  image {
		altText
		mainImageMobile {
			altText
			title
			localFile {
				childImageSharp {
					gatsbyImageData
				}
			}
		}
		tabletImage {
			localFile {
				childImageSharp {
					gatsbyImageData
				}
			}
		}
		laptopImage {
			localFile {
				childImageSharp {
					gatsbyImageData
				}
			}
		}
		desktopImage {
			localFile {
				childImageSharp {
					gatsbyImageData
				}
			}
		}
		widescreenImage {
			localFile {
				childImageSharp {
					gatsbyImageData
				}
			}
		}
	}

 *	Pass to this component like so:
 *
 *  <GatsbyImage {...imageGImage} />
 *
 *  *********  Query for images sourced from Shopify **********
 *
 *  image {
 *		altText
 *		gatsbyImageData(width: 860, height: 440)
 *	}
 *
 *  Images from Shopify wont be strucured like the ACF Image field group
 *  and will usually only need to pass one image so we can pass it
 *  explicity:
 *
 *  <GatsbyImage
 *		mainImageMobile={{
 *			mainImageMobile: category?.image,
 * 		}}
 *  />
 *
 */

// Returns gatsbyImageData given an image from WP or Shopify.

const getLocalFile = img => {
	if (img?.localFile) return img.localFile;

	return img;
};

const GatsbyImage = ({
	altText = '',
	mainImageMobile,
	tabletImage,
	laptopImage,
	desktopImage,
	widescreenImage,
	...gatsbyImageProps
}) => {
	if (!mainImageMobile) return null;

	const imageArray = [];

	if (widescreenImage) {
		imageArray.push({
			media: '(min-width: 1400px)',
			image: getImage(
				getLocalFile(widescreenImage)
			),
		});
	}

	if (desktopImage) {
		imageArray.push({
			media: '(min-width: 1200px)',
			image: getImage(
				getLocalFile(desktopImage)
			),
		});
	}

	if (laptopImage) {
		imageArray.push({
			media: '(min-width: 992px)',
			image: getImage(
				getLocalFile(laptopImage)
			),
		});
	}

	if (tabletImage) {
		imageArray.push({
			media: '(min-width: 768px)',
			image: getImage(
				getLocalFile(tabletImage)
			),
		});
	}

	// Determine if withArtDirection is used based on passed in images

	const images = imageArray.length > 0
		? withArtDirection(
			getImage(
				getLocalFile(mainImageMobile)
			),
			imageArray
		)
		: getImage(
			getLocalFile(mainImageMobile)
		);

	return (
		<GI
			alt={
				altText // overwrites all below altText properties
				|| mainImageMobile?.altText // Shopoify and wp images have this image property
				|| mainImageMobile?.title // fallback for wp altTxt
				|| ''
			}
			image={images}
			draggable={false}
			{...gatsbyImageProps}
		/>
	);
};

export default GatsbyImage;
